import { useState } from 'react';

import { Button, ModalWrapper } from '@braskai/ui-kit';

import { Close } from '@mui/icons-material';
import { Box, IconButton, Modal, Stack, useMediaQuery, useTheme } from '@mui/material';

import { Partners, usePricingExperimentExposure, useTrialPricingModalStore } from '@/entities/pricing';

import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { useInterfaceLanguage } from '@/shared/i18n';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PaymentInterval, Plan, Price } from '@/shared/types/Billing';

import { CUSTOM_PLAN_DATA_NEW } from '../../lib/constants';
import { usePricingCardsController } from '../../model/usePricingCardsController';
import { BillingPeriodTabs } from '../BillingPeriodTabs';
import { ExpandedPlansFeatures } from '../ExpandedPlansFeatures';
import { PricingCard } from '../PricingCard';
import { PricingHeader } from '../PricingHeader';
import { YearlyAdvantages } from '../YearlyAdvantages';

const SHEET_RADIUS = 20;
const SHEET_SIDE_GUTTER = { desktop: 80, mobile: 24 };
const SHEET_PADDING_X = { desktop: 20, mobile: 16 };
const MOBILE_CTA_INSET = 24;
// Match the comparison table's content width so plan columns and comparison columns line up.
const CONTENT_MAX_WIDTH = 1260;

const TrialPricingModalComponent = () => {
  const { t } = useInterfaceLanguage();
  const close = useTrialPricingModalStore((state) => state.close);
  const openedAutomatically = useTrialPricingModalStore((state) => state.openedAutomatically);
  const theme = useTheme();
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up('laptop'), { noSsr: true });
  const [chosenPlanId, setChosenPlanId] = useState<null | string>(null);

  const { paymentPeriod, plansGroupByCategory, products, yearlySelected, handleBuyPlanButtonClick, handleToggle } =
    usePricingCardsController({ defaultPaymentPeriod: PaymentInterval.Year, source: 'trialModal', setChosenPlanId });

  usePricingExperimentExposure(products, true, MixpanelEvents.pricingPageShown, openedAutomatically);

  useExecuteOneTime(() => {
    MixpanelEvents.trialPricingModalShown();
  });

  const handleClose = () => {
    MixpanelEvents.trialPricingModalDismissed();
    close();
  };

  const handleContinueWithFreeTrial = () => {
    MixpanelEvents.trialPricingModalContinueWithFreeTrialClick();
    handleClose();
  };

  const handlePlanGetStarted = (priceId: Price['id'], plan: Plan) => {
    MixpanelEvents.trialPricingModalPlanGetStartedClick({ period: paymentPeriod, plan: plan.category });
    void handleBuyPlanButtonClick(priceId, plan);
  };

  const plansByCategories = [...plansGroupByCategory, CUSTOM_PLAN_DATA_NEW];
  const gutter = isDesktopLayout ? SHEET_SIDE_GUTTER.desktop : SHEET_SIDE_GUTTER.mobile;
  const paddingX = isDesktopLayout ? SHEET_PADDING_X.desktop : SHEET_PADDING_X.mobile;
  const chromeOffset = isDesktopLayout ? 40 : MOBILE_CTA_INSET;

  return (
    <Modal
      sx={{ alignItems: 'flex-end', display: 'flex', justifyContent: 'center' }}
      open
      onClose={openedAutomatically ? undefined : handleClose}
    >
      <Stack
        sx={{
          // The scrollbar track would otherwise paint over the top-right radius.
          '&::-webkit-scrollbar': { display: 'none' },
          backgroundColor: 'common.white',
          borderTopLeftRadius: SHEET_RADIUS,
          borderTopRightRadius: SHEET_RADIUS,
          maxHeight: 'calc(100dvh - 40px)',
          overflowY: 'auto',
          position: 'relative',
          scrollbarWidth: 'none',
          width: `calc(100vw - ${gutter}px)`,
        }}
      >
        {openedAutomatically ? (
          <Button
            sx={{
              justifyContent: 'center',
              position: 'absolute',
              right: chromeOffset,
              top: chromeOffset,
              width: isDesktopLayout ? 'auto' : `calc(100% - ${chromeOffset * 2}px)`,
              zIndex: 3,
            }}
            data-testid="trial-pricing-modal-close"
            size="md"
            variant="outlined"
            onClick={handleContinueWithFreeTrial}
          >
            {t('pricing.trial.continue')}
          </Button>
        ) : (
          <IconButton
            aria-label={t('pricing.modal.closeAria')}
            data-testid="trial-pricing-modal-close"
            sx={{ color: 'grey.900', position: 'absolute', right: chromeOffset, top: chromeOffset, zIndex: 3 }}
            onClick={handleClose}
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        )}

        <Stack
          alignItems="center"
          gap={isDesktopLayout ? 32 : 24}
          paddingBottom={40}
          paddingTop={isDesktopLayout || !openedAutomatically ? 60 : 88}
          paddingX={paddingX}
        >
          <Stack alignItems="center" gap={24} sx={{ maxWidth: CONTENT_MAX_WIDTH, width: '100%' }}>
            <PricingHeader />
            <Stack alignItems="center" gap={isDesktopLayout ? 16 : 24} sx={{ width: '100%' }}>
              <BillingPeriodTabs yearlySelected={yearlySelected} onToggle={handleToggle} />
              {/* The yearly-only perks banner is hidden while Monthly is selected (Figma). */}
              {yearlySelected && <YearlyAdvantages />}
            </Stack>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gap: 12,
              gridTemplateColumns: isDesktopLayout ? 'repeat(4, 1fr)' : '1fr',
              maxWidth: CONTENT_MAX_WIDTH,
              width: '100%',
            }}
          >
            {plansByCategories.map((plansByCategory) => (
              <PricingCard
                key={plansByCategory.category}
                chosenPlanId={chosenPlanId}
                plansByCategory={plansByCategory}
                selectedPaymentPeriod={paymentPeriod}
                onActionButtonClick={handlePlanGetStarted}
              />
            ))}
          </Box>
        </Stack>

        <ExpandedPlansFeatures
          yearlySelected={yearlySelected}
          onBuyPlan={handlePlanGetStarted}
          onToggle={handleToggle}
        />

        <Partners />
      </Stack>
    </Modal>
  );
};

export const TrialPricingModal = () => (
  <ModalWrapper hook={useTrialPricingModalStore}>
    <TrialPricingModalComponent />
  </ModalWrapper>
);
